import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { m } from "framer-motion";
import { Shield, Phone, TriangleAlert, Pill, Sparkles, Lock, Printer, CheckCircle2, Clock, Utensils, Stethoscope, Syringe, UserCheck, MapPin } from "lucide-react";
import {
  usePublicShare,
  useAcknowledgeShare,
  type ScopedChildView,
  type ScopedRoutine,
} from "../lib/api/usePublicShare";
import { SeverityBadge, SEVERITY_CARD_CLASSES } from "../components/ui/SeverityBadge";
import { ChildAvatar } from "../components/ui/ChildAvatar";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { PageLoader } from "../components/ui/Spinner";
import { WaveDivider } from "../components/decor";
import { AllergyPlanPanel } from "../components/child/AllergyPlanPanel";
import { fadeUp } from "../lib/motion";
import { getEmergencyLocationLink, copyToClipboard } from "../lib/geo";
import { useToast } from "../lib/toast/ToastProvider";
import { FREQUENCY_LABELS } from "@shared/constants/frequency";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";

/**
 * Mount-triggered reveal — deliberately NOT the shared whileInView `Reveal`.
 * A caregiver may print this sheet (or read it in an emergency) without ever
 * scrolling; whileInView would leave below-fold sections at opacity 0, which
 * must never happen here. One gentle fade-up for every section, no stagger.
 * The print-media overrides beat framer's inline styles (!important), so the
 * sheet always prints at full ink even if printed mid-animation.
 */
function SectionReveal({ children }: { children: React.ReactNode }) {
  return (
    <m.div
      className="print:!opacity-100 print:!transform-none"
      variants={fadeUp}
      initial="hidden"
      animate="show"
    >
      {children}
    </m.div>
  );
}

export function CaregiverView() {
  const [, params] = useRoute("/care/:token");
  const token = params?.token;
  const { data, isLoading, error } = usePublicShare(token);
  const ackMutation = useAcknowledgeShare(token);
  const [acked, setAcked] = useState(false);
  useEffect(() => {
    if (data?.meta.acknowledged) setAcked(true);
  }, [data]);

  if (!token) {
    return <Centered>Missing share token</Centered>;
  }

  if (isLoading) {
    return <PageLoader label="Loading care profile…" />;
  }

  if (error) {
    const code = error.code;
    const title =
      code === "LINK_EXPIRED"
        ? "This link has expired"
        : code === "LINK_INACTIVE"
          ? "This link has been revoked"
          : "This link is not valid";
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
        <Card className="max-w-md p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-neutral-400" aria-hidden />
          <h1 className="mt-4 font-display text-xl font-semibold text-neutral-900">{title}</h1>
          <p className="mt-2 text-sm text-neutral-600">
            This care profile link is invalid, expired, or no longer active. Please
            ask the parent for a new link.
          </p>
        </Card>
      </main>
    );
  }

  if (!data) return null;

  return (
    <main className="min-h-screen bg-neutral-50 px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="no-print flex flex-wrap justify-end gap-2">
          <EmergencyLocationButton />
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" aria-hidden /> Print / Save as PDF
          </Button>
        </div>
        <SectionReveal>
          <header className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-700 to-indigo-700 p-6 text-white shadow-soft">
            <WaveDivider fill="#ffffff" className="absolute inset-x-0 bottom-0 h-8 opacity-[0.07]" />
            <div className="relative">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-sky-50">
                <Shield className="h-3.5 w-3.5" aria-hidden /> Shared via Leena's List
              </div>
              <div className="mt-2 flex items-center gap-4">
                {/* Draws the nickname initial: photos are off (PHOTO_UPLOADS_ENABLED),
                    the server sends none and ChildAvatar ignores any it is given. */}
                <ChildAvatar
                  name={data.child.name}
                  photoUrl={data.child.photoUrl}
                  className="h-16 w-16 flex-shrink-0 ring-2 ring-white/25"
                  textClassName="text-2xl"
                />
                <div>
                  <h1 className="font-display text-3xl font-bold tracking-tight">
                    {data.child.name}
                  </h1>
                  {/* BRIEF §5.2: the server includes legalName ONLY when the
                      parent ticked "Include legal name" on this link. */}
                  {data.child.legalName && (
                    <p className="mt-1 text-sm text-sky-50">
                      Legal name:{" "}
                      <span className="font-semibold text-white">{data.child.legalName}</span>
                    </p>
                  )}
                  {data.visibleSections.basic && (
                    <p className="mt-1 text-sm text-sky-50">
                      {data.child.age !== null && data.child.age !== undefined
                        ? `Age ${data.child.age}`
                        : "Age not set"}
                      {data.child.grade ? ` · ${data.child.grade}` : ""}
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-3 text-xs text-sky-100">
                Shared with: {data.meta.caregiverName}
              </p>
            </div>
          </header>
        </SectionReveal>

        {data.emergencyContacts && (
          <SectionReveal>
            <EmergencyContactsCard contacts={data.emergencyContacts} />
          </SectionReveal>
        )}

        {data.allergies && data.allergies.length > 0 && (
          <SectionReveal>
            <AllergiesCard allergies={data.allergies} />
          </SectionReveal>
        )}

        {data.medications && data.medications.length > 0 && (
          <SectionReveal>
            <MedicationsCard medications={data.medications} />
          </SectionReveal>
        )}

        {data.carePreferences && (
          <SectionReveal>
            <CarePreferencesCard prefs={data.carePreferences} />
          </SectionReveal>
        )}

        {data.routines && data.routines.length > 0 && (
          <SectionReveal>
            <RoutinesCard routines={data.routines} />
          </SectionReveal>
        )}

        {data.feeding && (
          <SectionReveal>
            <FeedingCard feeding={data.feeding} />
          </SectionReveal>
        )}

        {data.immunizations && data.immunizations.length > 0 && (
          <SectionReveal>
            <ImmunizationsCard immunizations={data.immunizations} />
          </SectionReveal>
        )}

        {/* The server leaves both out while PICKUP_CONSENT_ENABLED is off; the
            switch is checked here too, so nothing could ever render them. */}
        {PICKUP_CONSENT_ENABLED && (data.authorizedPickups || data.careAuthorization) && (
          <SectionReveal>
            <PickupCard
              pickups={data.authorizedPickups ?? []}
              authorization={data.careAuthorization}
            />
          </SectionReveal>
        )}

        {data.medicalHistory && (
          <SectionReveal>
            <MedicalHistoryCard history={data.medicalHistory} />
          </SectionReveal>
        )}

        {data.visibleSections.basic && data.child.additionalNotes && (
          <SectionReveal>
            <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
                Additional Notes
              </h2>
              <p className="mt-3 whitespace-pre-line text-sm text-neutral-800">
                {data.child.additionalNotes}
              </p>
            </section>
          </SectionReveal>
        )}

        {data.visibleSections.basic &&
          data.child.isEnrolled &&
          (data.child.schoolName || data.child.teacherName || data.child.schoolNursePhone) && (
            <SectionReveal>
              <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
                  School
                </h2>
                {data.child.schoolName && (
                  <p className="mt-3 text-sm font-medium text-neutral-900">
                    {data.child.schoolName}
                  </p>
                )}
                {data.child.schoolPhone && (
                  <p className="text-sm text-neutral-600">{data.child.schoolPhone}</p>
                )}
                {data.child.teacherName && (
                  <p className="mt-2 text-sm text-neutral-600">
                    Teacher: <span className="text-neutral-900">{data.child.teacherName}</span>
                  </p>
                )}
                {/* Dialable like the emergency contacts — in a school-hours
                    incident the nurse is who a caregiver actually calls. */}
                {data.child.schoolNursePhone && (
                  <a
                    href={`tel:${data.child.schoolNursePhone}`}
                    className="mt-1 inline-flex items-center gap-1 py-1 text-sm font-semibold text-red-700 hover:underline"
                  >
                    <Phone className="h-3 w-3" aria-hidden /> School nurse:{" "}
                    {data.child.schoolNursePhone}
                  </a>
                )}
              </section>
            </SectionReveal>
          )}

        <div className="no-print">
          {acked ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
              <CheckCircle2 className="h-5 w-5" aria-hidden /> Thanks — you've confirmed you read this.
            </div>
          ) : (
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              isLoading={ackMutation.isPending}
              onClick={() => ackMutation.mutate(undefined, { onSuccess: () => setAcked(true) })}
            >
              {!ackMutation.isPending && <CheckCircle2 className="h-5 w-5" aria-hidden />}
              {ackMutation.isPending ? "Confirming…" : "I've read this care sheet"}
            </Button>
          )}
        </div>
        <footer className="pt-4 text-center text-xs text-neutral-400">
          Last updated {new Date(data.meta.lastUpdated).toLocaleDateString()}
          <br />
          <span className="italic">
            This information is provided securely via Leena's List. All access is
            logged with timestamps.
          </span>
        </footer>
      </div>
    </main>
  );
}

/**
 * Copies a Google Maps link to the caregiver's current position, so they can
 * text the parent exactly where the child is. Nothing is sent to the server and
 * nothing is stored — the coordinates reach only the caregiver's clipboard.
 */
function EmergencyLocationButton() {
  const { publish } = useToast();
  const [isLocating, setIsLocating] = useState(false);

  async function share() {
    setIsLocating(true);
    try {
      const url = await getEmergencyLocationLink();
      const copied = await copyToClipboard(url);
      publish(
        copied
          ? {
              variant: "success",
              title: "Location copied",
              description: "Paste it into a text or call to share where you are.",
            }
          : // Clipboard blocked (in-app browser, insecure origin) — show the
            // link itself so it can still be copied by hand.
            { variant: "info", title: "Your location link", description: url, duration: 20000 },
      );
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't get your location",
        description:
          err instanceof Error ? err.message : "Your location could not be determined right now.",
      });
    } finally {
      setIsLocating(false);
    }
  }

  return (
    <Button
      variant="destructiveOutline"
      size="sm"
      onClick={() => void share()}
      isLoading={isLocating}
    >
      {!isLocating && <MapPin className="h-4 w-4" aria-hidden />}
      {isLocating ? "Getting location…" : "Share my location"}
    </Button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 text-center text-neutral-600">
      <div>{children}</div>
    </main>
  );
}

function EmergencyContactsCard({
  contacts,
}: {
  contacts: NonNullable<ScopedChildView["emergencyContacts"]>;
}) {
  return (
    <section className="rounded-3xl border-2 border-red-200 bg-red-50 p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Phone className="h-5 w-5 text-red-700" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-red-800">
          Emergency Contacts
        </h2>
      </div>
      <div className="mt-4 space-y-4">
        {contacts.primary.name && (
          <ContactBlock label="Primary" contact={contacts.primary} />
        )}
        {contacts.secondary.name && (
          <ContactBlock label="Secondary" contact={contacts.secondary} />
        )}
      </div>
    </section>
  );
}

function ContactBlock({
  label,
  contact,
}: {
  label: string;
  contact: { name: string | null; phone: string | null; relationship: string | null };
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700">
        {label}
      </p>
      <p className="text-base font-semibold text-neutral-900">{contact.name}</p>
      {contact.phone && (
        <a
          href={`tel:${contact.phone}`}
          className="mt-1 inline-flex items-center gap-1 py-1 text-sm font-semibold text-red-700 hover:underline"
        >
          <Phone className="h-3 w-3" aria-hidden /> {contact.phone}
        </a>
      )}
      {contact.relationship && (
        <p className="text-xs text-neutral-600">{contact.relationship}</p>
      )}
    </div>
  );
}

function PickupCard({
  pickups,
  authorization,
}: {
  pickups: NonNullable<ScopedChildView["authorizedPickups"]>;
  authorization: ScopedChildView["careAuthorization"];
}) {
  const authorized = pickups.filter((p) => p.isActive);
  const revoked = pickups.filter((p) => !p.isActive);

  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <UserCheck className="h-5 w-5 text-emerald-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Pickup &amp; Authorization
        </h2>
      </div>

      {authorization && (
        <div
          className={`mt-4 rounded-xl p-3 ${
            authorization.consentToTreat
              ? "bg-emerald-50 text-emerald-900"
              : "bg-amber-50 text-amber-900"
          }`}
        >
          <p className="text-sm font-medium">
            {authorization.consentToTreat
              ? "You are authorized to consent to emergency medical treatment."
              : "You are NOT authorized to consent to emergency medical treatment."}
          </p>
          {authorization.consentGrantedByName && authorization.consentToTreat && (
            <p className="mt-0.5 text-xs">
              Authorized by {authorization.consentGrantedByName}
            </p>
          )}
          {(authorization.insuranceCarrier || authorization.preferredHospital) && (
            <dl className="mt-2 space-y-0.5 text-xs">
              {authorization.insuranceCarrier && (
                <div>
                  <dt className="inline font-semibold">Insurance: </dt>
                  <dd className="inline">
                    {authorization.insuranceCarrier}
                    {authorization.insurancePolicyNumber
                      ? ` · Policy ${authorization.insurancePolicyNumber}`
                      : ""}
                    {authorization.insuranceGroupNumber
                      ? ` · Group ${authorization.insuranceGroupNumber}`
                      : ""}
                  </dd>
                </div>
              )}
              {authorization.preferredHospital && (
                <div>
                  <dt className="inline font-semibold">Preferred hospital: </dt>
                  <dd className="inline">{authorization.preferredHospital}</dd>
                </div>
              )}
            </dl>
          )}
          {authorization.notes && (
            <p className="mt-1 text-xs italic">{authorization.notes}</p>
          )}
        </div>
      )}

      {authorized.length > 0 && (
        <>
          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            May collect this child
          </h3>
          <ul className="mt-2 space-y-2">
            {authorized.map((p) => (
              <li key={p.id} className="rounded-xl bg-emerald-50/60 p-3">
                <p className="text-sm font-semibold text-neutral-900">
                  {p.name}
                  {p.relationship && (
                    <span className="ml-2 font-normal text-neutral-600">
                      {p.relationship}
                    </span>
                  )}
                </p>
                {p.phone && <p className="text-xs text-neutral-700">{p.phone}</p>}
                {p.notes && <p className="mt-1 text-xs italic text-neutral-600">{p.notes}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {/*
        Revoked people are shown deliberately. At the door, "this person is no
        longer allowed" is far more useful than their silent absence, which is
        indistinguishable from someone the parent simply never listed.
      */}
      {revoked.length > 0 && (
        <>
          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-red-700">
            Not authorized to collect
          </h3>
          <ul className="mt-2 space-y-2">
            {revoked.map((p) => (
              <li key={p.id} className="rounded-xl bg-red-50 p-3">
                <p className="text-sm font-semibold text-neutral-900">
                  {p.name}
                  {p.relationship && (
                    <span className="ml-2 font-normal text-neutral-600">
                      {p.relationship}
                    </span>
                  )}
                </p>
                {p.notes && <p className="mt-1 text-xs italic text-neutral-600">{p.notes}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {authorized.length === 0 && revoked.length === 0 && (
        <p className="mt-3 text-sm text-neutral-600">
          No pickup contacts have been listed.
        </p>
      )}
    </section>
  );
}

function AllergiesCard({
  allergies,
}: {
  allergies: NonNullable<ScopedChildView["allergies"]>;
}) {
  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <TriangleAlert className="h-5 w-5 text-amber-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Allergies &amp; Restrictions
        </h2>
      </div>
      <ul className="mt-4 space-y-3">
        {allergies.map((allergy) => (
          <li
            key={allergy.id}
            className={`rounded-xl p-4 ${SEVERITY_CARD_CLASSES[allergy.severity]}`}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-neutral-900">
                {allergy.name}
              </h3>
              <SeverityBadge severity={allergy.severity} />
            </div>
            {allergy.description && (
              <p className="mt-1 text-sm text-neutral-700">{allergy.description}</p>
            )}
            <AllergyPlanPanel
              severity={allergy.severity}
              reactionSymptoms={allergy.reactionSymptoms}
              treatment={allergy.treatment}
              epinephrineLocation={allergy.epinephrineLocation}
              rescueMedicationName={allergy.rescueMedicationName}
            />
            {allergy.emergencyAction && (
              <div className="mt-2 rounded-md bg-white/70 p-2">
                <p className="text-xs">
                  <span className="font-semibold text-red-800">Emergency Action: </span>
                  <span className="text-neutral-800">{allergy.emergencyAction}</span>
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function MedicationsCard({
  medications,
}: {
  medications: NonNullable<ScopedChildView["medications"]>;
}) {
  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Pill className="h-5 w-5 text-neutral-700" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Medications &amp; Treatments
        </h2>
      </div>
      <ul className="mt-4 space-y-3">
        {medications.map((med) => (
          <li key={med.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <h3 className="text-base font-semibold text-neutral-900">{med.name}</h3>
            <p className="mt-1 text-sm text-neutral-700">
              <span className="font-medium">Dosage:</span> {med.dosage}
              <span className="mx-2 text-neutral-400">·</span>
              {FREQUENCY_LABELS[med.frequency as keyof typeof FREQUENCY_LABELS] ?? med.frequency}
            </p>
            {med.notes && (
              <p className="mt-2 text-xs italic text-neutral-600">{med.notes}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function CarePreferencesCard({
  prefs,
}: {
  prefs: NonNullable<ScopedChildView["carePreferences"]>;
}) {
  const fields = [
    { label: "Calming Strategies", value: prefs.calmingStrategies },
    { label: "Comfort Items", value: prefs.comfortItems },
    { label: "Communication Style", value: prefs.communicationStyle },
    { label: "Behavioral Notes", value: prefs.behavioralNotes },
  ].filter((f) => f.value !== null && f.value !== "");

  if (fields.length === 0) return null;

  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-neutral-700" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Care Preferences
        </h2>
      </div>
      <dl className="mt-4 space-y-4">
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {field.label}
            </dt>
            <dd className="mt-1 whitespace-pre-line text-sm text-neutral-800">
              {field.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const DAYPART_LABELS: Record<ScopedRoutine["daypart"], string> = {
  morning: "Morning",
  midday: "Midday",
  afternoon: "Afternoon",
  evening: "Evening",
  bedtime: "Bedtime",
};

function RoutinesCard({ routines }: { routines: NonNullable<ScopedChildView["routines"]> }) {
  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-indigo-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Daily Routines
        </h2>
      </div>
      <ul className="mt-4 space-y-3">
        {routines.map((r) => (
          <li key={r.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-neutral-900">{r.label}</h3>
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                {DAYPART_LABELS[r.daypart]}
                {r.timeText ? ` · ${r.timeText}` : ""}
              </span>
            </div>
            {r.details && <p className="mt-1 text-sm text-neutral-700">{r.details}</p>}
            {r.notes && <p className="mt-2 text-xs italic text-neutral-600">{r.notes}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function FeedingCard({ feeding }: { feeding: NonNullable<ScopedChildView["feeding"]> }) {
  const fields = [
    { label: "Diet Type", value: feeding.dietType },
    { label: "Restrictions", value: feeding.restrictions },
    { label: "Likes", value: feeding.likes },
    { label: "Dislikes", value: feeding.dislikes },
    { label: "Portions & Appetite", value: feeding.portionNotes },
    { label: "Feeding Schedule", value: feeding.scheduleNotes },
    { label: "Bottle / Formula", value: feeding.bottleFormula },
    { label: "Utensils & Setup", value: feeding.utensilsNotes },
  ].filter((f) => f.value !== null && f.value !== "");

  if (fields.length === 0) return null;

  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Utensils className="h-5 w-5 text-amber-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Feeding &amp; Diet
        </h2>
      </div>
      <dl className="mt-4 space-y-4">
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {field.label}
            </dt>
            <dd className="mt-1 whitespace-pre-line text-sm text-neutral-800">{field.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ImmunizationsCard({
  immunizations,
}: {
  immunizations: NonNullable<ScopedChildView["immunizations"]>;
}) {
  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Syringe className="h-5 w-5 text-violet-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Immunizations
        </h2>
      </div>
      <ul className="mt-4 space-y-3">
        {immunizations.map((imm) => (
          <li key={imm.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-neutral-900">{imm.vaccineName}</h3>
              {imm.dateAdministered && (
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                  Given {imm.dateAdministered}
                </span>
              )}
              {imm.nextDueDate && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  Next due {imm.nextDueDate}
                </span>
              )}
            </div>
            {imm.provider && <p className="mt-1 text-sm text-neutral-700">{imm.provider}</p>}
            {imm.notes && <p className="mt-2 text-xs italic text-neutral-600">{imm.notes}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function MedicalHistoryCard({
  history,
}: {
  history: NonNullable<ScopedChildView["medicalHistory"]>;
}) {
  const physician = [history.primaryPhysicianName, history.primaryPhysicianPhone]
    .filter(Boolean)
    .join(" · ");
  const dentist = [history.dentistName, history.dentistPhone].filter(Boolean).join(" · ");
  const fields = [
    { label: "Primary Physician", value: physician || null },
    { label: "Dentist", value: dentist || null },
    { label: "Blood Type", value: history.bloodType },
    { label: "Conditions", value: history.conditions },
    { label: "Past Illnesses", value: history.pastIllnesses },
    { label: "Hospitalizations / Surgeries", value: history.hospitalizations },
    { label: "Insurance", value: history.insuranceNotes },
    { label: "Notes", value: history.notes },
  ].filter((f) => f.value !== null && f.value !== "");

  if (fields.length === 0) return null;

  return (
    <section className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-soft">
      <div className="flex items-center gap-2">
        <Stethoscope className="h-5 w-5 text-violet-600" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
          Medical History
        </h2>
      </div>
      <dl className="mt-4 space-y-4">
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {field.label}
            </dt>
            <dd className="mt-1 whitespace-pre-line text-sm text-neutral-800">{field.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
